import React, {createContext, useContext, useState, ReactNode, useCallback, useEffect} from 'react';
import { Option, SocialAccountOption, SocialPostTypeOption } from '../types/socialTypes';
import { getSocialOptions } from '../queries/socialQueries';

export const defaultLanguages = [{ key: 'italian', label: "IT" }, { key: 'english', label: "EN" }];
export const defaultStyles = [
    { label: "Fan", key: "fan" },
    { label: "Archivist", key: "archivist" },
    { label: "Journalistic", key: 'journalistic' },
    { label: "Commentator", key: 'commentator' }
];

export interface SocialOptions {
    socialPlatforms: Option[];
    socialAccounts: SocialAccountOption[];
    socialPostTypes: SocialPostTypeOption[];
}

export interface PlatformState {
    socialAccount: Option | null;
    socialPostType: Option | null;
    selectedLanguage: { key: string; label: string };
    selectedStyle: { key: string; label: string };
    socialFormState: any;
}

interface SocialPublicationContextType {
    platformStates: Record<string, PlatformState>;
    socialOptions: SocialOptions | null;
    updatePlatformState: (platformId: string, updates: Partial<PlatformState>) => void;
    updatePlatformFormState: (platformId: string, field: string, value: any) => void;
    loadSocialOptions: () => Promise<SocialOptions>; // Nuova funzione nell'interfaccia
    resetContext: () => void;
}

export const initialPlatformState: PlatformState = {
    socialAccount: null,
    socialPostType: null,
    selectedLanguage: defaultLanguages[0],
    selectedStyle: defaultStyles[2],
    socialFormState: { title: '', description: '', caption: '' }
};

const SocialPublicationContext = createContext<SocialPublicationContextType | undefined>(undefined);

export const SocialPublicationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [platformStates, setPlatformStates] = useState<Record<string, PlatformState>>({});
    const [socialOptions, setSocialOptionsState] = useState<SocialOptions | null>(null);

    const updatePlatformState = useCallback((platformId: string, updates: Partial<PlatformState>) => {
        setPlatformStates(prev => ({
            ...prev,
            [platformId]: {
                ...(prev[platformId] || initialPlatformState),
                ...updates
            }
        }));
    }, []);

    const updatePlatformFormState = useCallback((platformId: string, field: string, value: any) => {
        setPlatformStates(prev => {
            const currentState = prev[platformId] || initialPlatformState;
            return {
                ...prev,
                [platformId]: {
                    ...currentState,
                    socialFormState: {
                        ...currentState.socialFormState,
                        [field]: value
                    }
                }
            };
        });
    }, []);

    const loadSocialOptions = useCallback(async () => {
        const opts = await getSocialOptions();
        setSocialOptionsState(opts);
        return opts;
    }, []);

    const resetContext = useCallback(() => {
        setPlatformStates({});
        setSocialOptionsState(null);
    }, []);

    return (
        <SocialPublicationContext.Provider value={{
            platformStates,
            socialOptions,
            updatePlatformState,
            updatePlatformFormState,
            loadSocialOptions,
            resetContext
        }}>
            {children}
        </SocialPublicationContext.Provider>
    );
};

export const useSocialPublication = () => {
    const context = useContext(SocialPublicationContext);
    if (!context) throw new Error("useSocialPublication must be used within a SocialPublicationProvider");
    return context;
};